import { formatBudgetCurrency } from "./expansionBudgetReportData";
import { EXPANSION_BUDGET_CHART_TOP_N } from "./expansionBudgetReportData";

const CHART_COLORS = [
  "#2563eb",
  "#7c3aed",
  "#db2777",
  "#ea580c",
  "#ca8a04",
  "#16a34a",
  "#0891b2",
  "#4f46e5",
  "#c026d3",
  "#0d9488",
  "#dc2626",
  "#65a30d",
];

export const COMMERCIAL_REPORT_METRICS = [
  { key: "total_mrc", label: "Total MRC" },
  { key: "project_count", label: "Project Count" },
];

export function formatCommercialDays(value) {
  if (value == null || !Number.isFinite(Number(value))) return "—";
  const n = Number(value);
  return `${n.toLocaleString(undefined, { maximumFractionDigits: 1 })} days`;
}

export function formatCommercialMetric(value, metricKey) {
  if (metricKey === "project_count") {
    if (value == null || !Number.isFinite(Number(value))) return "0";
    return Number(value).toLocaleString();
  }
  return formatBudgetCurrency(value);
}

export function commercialPhaseChartData(byPhase, metricKey, topN = EXPANSION_BUDGET_CHART_TOP_N) {
  const rows = Array.isArray(byPhase) ? byPhase : [];
  const entries = rows
    .map((row) => ({
      name: row.phase,
      value:
        metricKey === "project_count"
          ? row.project_count ?? 0
          : row.total_mrc ?? 0,
    }))
    .filter((e) => e.value > 0)
    .sort((a, b) => b.value - a.value);

  const total = entries.reduce((sum, e) => sum + e.value, 0);
  const colored = entries.map((entry, index) => ({
    ...entry,
    color: CHART_COLORS[index % CHART_COLORS.length],
    pct: total !== 0 ? entry.value / total : 0,
    faded: false,
  }));

  if (colored.length <= topN) return colored;

  const top = colored.slice(0, topN);
  const rest = colored.slice(topN);
  const otherValue = Math.round(rest.reduce((sum, e) => sum + e.value, 0) * 100) / 100;
  if (otherValue === 0) return top;

  return [
    ...top,
    {
      name: `Other (${rest.length})`,
      value: otherValue,
      color: "#cbd5e1",
      pct: total !== 0 ? otherValue / total : 0,
      faded: true,
    },
  ];
}

export function summarizeCommercialReport(byPhase, metricKey) {
  const rows = Array.isArray(byPhase) ? byPhase : [];
  if (metricKey === "project_count") {
    const total = rows.reduce((sum, r) => sum + (r.project_count ?? 0), 0);
    return { total, withData: rows.filter((r) => (r.project_count ?? 0) > 0).length };
  }
  const total = rows.reduce((sum, r) => sum + (r.total_mrc ?? 0), 0);
  return {
    total: Math.round(total * 100) / 100,
    withData: rows.filter((r) => (r.total_mrc ?? 0) > 0).length,
  };
}

export function toCommercialPhaseGridRows(byPhase) {
  return (Array.isArray(byPhase) ? byPhase : []).map((row) => ({
    id: row.phase,
    phase: row.phase,
    projectCount: row.project_count ?? 0,
    totalMrc: row.total_mrc ?? 0,
    projectsWithMrc: row.projects_with_mrc ?? 0,
    avgDaysInPhase: row.avg_days_in_phase,
    phaseStays: row.phase_stays ?? 0,
  }));
}

export function toCommercialProjectGridRows(projects) {
  return (Array.isArray(projects) ? projects : []).map((row) => ({
    id: String(row.id),
    workOrder: row.work_order || "",
    customer: row.customer || "",
    project: row.project || "",
    status: row.status || "",
    phase: row.phase || "",
    subphase: row.subphase || "",
    mrc: row.mrc,
    daysInCurrentPhase: row.days_in_current_phase,
  }));
}
